import { Fragment, useContext, useEffect, useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { nanoid } from 'nanoid';
import { useHistory } from 'react-router-dom';
import { Button, FAB } from '@upmc/ims.component-library';
import axiosInstance from '../../../services/axios/axios';
import { UserContext } from 'context/User/UserContext';
import { WrapImsPacket } from '../../../store/utility';
import { DataTable } from '../../../components/UI/MDC/DataTable/DataTable';
import Card from '../../../components/UI/MDC/Card/Card';
import { Body1 } from '../../../components/UI/MDC/Typography/Typography';
import WithLoadingHandler from '../../../hoc/withLoadingHandler/withLoadingHandler';
import { CSS_CLASSES } from './_constants';
import { ACTION_TYPES } from '../../../components/UI/MDC/DataTable/_types';
import classes from './ExistingServiceAccount.module.scss';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Tooltip } from '../../../components/UI/MDC/Tooltip/Tooltip';
import { CONTENT_ALIGNMENT } from '../../../components/UI/MDC/Tooltip/_types';
import Icon from '../../../components/UI/MDC/Icon/Icon';
import Base from '../../Base/Base';
import { raiseErrorBanner } from '../../../components/MessageDispatch/messageDispatch';
import { clearMessages } from '../../../store/ancillary/actions';
import { deepCloneObject } from '../../../store/utility';
import { RequestTypes } from '../../../constants/enums';

const ExistingServiceAccount = () => {
	const [sUserName, setUserName] = useState();
	const [sRequestType, setRequestType] = useState();
	const [sUserDepartment, setUserDepartment] = useState();
	const [sUserHospital, setUserHospital] = useState();
	const [sUserEmail, setUserEmail] = useState();
	const [sUserAccountType, setUserAccountType] = useState();
	const [sUsersServiceAccounts, setUsersServiceAccounts] = useState([]);
	const [sRedirect, setRedirect] = useState(false); // boolean to redirect if user does not have an existing service account
	const [sIsLoading, setIsLoading] = useState(false); // boolean for validation loading animation
	const dispatch = useDispatch();
	const history = useHistory();
	const [sOpenTerminateModal, setOpenTerminateModal] = useState(false); // boolean to show the terminate service account modal
	const [sOpenReactivateModal, setOpenReactivateModal] = useState(false); // boolean to show the reactivate service account modal
	const [sJustification, setJustification] = useState([]);
	const [sHideControl, setHideControl] = useState(false); // boolean to disable the terminate/reactivate functionality for users without appropriate permission (owner,owner's manager, owner's mega-manager)
	const [sRowInfo, setRowInfo] = useState();
	const [sRequestTrackingNumber, setRequestTrackingNumber] = useState();
	const [
		sReactivateRequestTrackingNumber,
		setReactivateRequestTrackingNumber,
	] = useState();
	const [sfilter, setFilter] = useState('');
	const [sShowFilter, setShowFilter] = useState(false); // Used to filter service accounts
	const [
		sUsersServiceAccountApplications,
		setUsersServiceAccountApplications,
	] = useState([]);

	/**
	 * @type {import('context/User/UserContext').ContextUser}
	 */
	const contextUser = useContext(UserContext);
	const user = contextUser.userInfo.allowImpersonation
		? contextUser.userInfo.impersonationProfile ??
		  contextUser.userInfo.userProfile
		: contextUser.userInfo.userProfile;

	const rowHeaders = ['NetworkID', 'FamiliarESOID', 'AccountType'];

	//Application Header info
	const appRowHeaders = ['Application Name'];

	const iconLegend = [
		{
			BindTo: 1,
			ValuePrimary: 'Modify Service Account',
		},
		{
			BindTo: 0,
			ValuePrimary: 'Terminate Service Account',
		},
		{
			BindTo: 2,
			ValuePrimary: 'Reactivate Service Account',
		},
	];

	//#region state parameters
	/**
	 * User specified parameter(s) from state.
	 */
	const location = useLocation();
	const requesteeESOFIID = location.state['FI_ID'].Value;
	const requesteeFamiliarESOID = location.state['UPMC_E-Sign-On_ID'].Value;
	const ServiceAccountSearch = location.Service_Account_Search;

	useEffect(() => {
		// Filtering the service accounts that match the search criterea
		const filteredServiceAccounts = deepCloneObject(sUsersServiceAccounts);
		if (sfilter) {
			filteredServiceAccounts.forEach(SA => {
				if (SA.Cells.NetworkID.Value) {
					SA.FilteredOut =
						!SA.Cells.NetworkID.Value.toLowerCase().includes(
							sfilter,
						);
				} else {
					SA.FilteredOut = true;
				}
			});
		} else {
			filteredServiceAccounts.forEach(SA => {
				SA.FilteredOut = false;
			});
		}
		setUsersServiceAccounts(filteredServiceAccounts);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [sfilter]);

	/**
	 * Effect on component mount. Queries the current user's information and performs initial page population accordingly.
	 * @returns void. No clean-up performed.
	 */
	useEffect(() => {
		dispatch(clearMessages());
		setIsLoading(true);
		pageLoadHandler(user);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user]);

	let pageLoadHandler = currentUser => {
		let payload = {
			Requestee_ESOFIID: requesteeESOFIID,
			Requestor_ESOFIID: currentUser.ID,
		};

		// Checking to see if a self request or for others
		requesteeESOFIID === String(currentUser.ID)
			? setRequestType('For_Myself')
			: setRequestType('For_Other');

		// Populating page with requestee's service account
		if (requesteeESOFIID && currentUser.ID) {
			axiosInstance
				.post(
					'api/ExistingServiceAccount/Populate',
					WrapImsPacket(payload),
				)
				.then(response => {
					const payload = response.data.payload;
					setUserName(payload.User);
					setUserDepartment(payload.Department);
					setUserHospital(payload.Hospital);
					setUserEmail(payload.Email);
					setUserAccountType(payload.Account_Type);
					setHideControl(payload.Hide_Terminate);
					if (payload.OwnerAccounts != null) {
						const accounts = payload.OwnerAccounts.map(row => {
							const validation = {
								Key: String(true),
								Value: null,
							};
							if (row.isReactivatable) {
								let rowObject = {
									ActionCells: [
										{
											BindTo: 2,
											Disabled: false,
											Key: nanoid(),
											Text: 'Reactivate Service Account',
										},
									],
									Key: nanoid(),
									Cells: {
										NetworkID: {
											Value: row.Name,
											Validation: validation,
										},
										FamiliarESOID: {
											Value: row.Familiar_ESO_ID,
											Validation: validation,
										},
										ESOFIID: {
											Value: row.ESOFIID,
											Validation: validation,
										},
										AccountType: {
											Value: row.Description,
											Validation: validation,
										},
									},
									AdditionalInfo: {
										OwnerEmail: row.Owner_Email,
										OwnerEmployeeID: row.Owner_Employee_ID,
										OwnerPhone: row.Owner_Phone,
										AccountType: row.AccountType,
										UserType: row.UserType,
									},
								};

								return rowObject;
							} else {
								let rowObject = {
									ActionCells: [
										{
											BindTo: 1,
											Disabled: false,
											Key: nanoid(),
											Text: 'Modify Service Account',
										},
										{
											BindTo: 0,
											Disabled: false,
											Key: nanoid(),
											Text: 'Terminate Service Account',
										},
									],
									Key: nanoid(),
									Cells: {
										NetworkID: {
											Value: row.Name,
											Validation: validation,
										},
										FamiliarESOID: {
											Value: row.Familiar_ESO_ID,
											Validation: validation,
										},
										ESOFIID: {
											Value: row.ESOFIID,
											Validation: validation,
										},
										AccountType: {
											Value: row.Description,
											Validation: validation,
										},
									},
									AdditionalInfo: {
										OwnerEmail: row.Owner_Email,
										OwnerEmployeeID: row.Owner_Employee_ID,
										OwnerPhone: row.Owner_Phone,
										AccountType: row.AccountType,
										UserType: row.UserType,
									},
								};
								//Automatically redirect to the modify page if requestor choose a service account from the list of search results
								if (ServiceAccountSearch) {
									//Matching the searched service account
									if (
										requesteeFamiliarESOID ===
										row.Familiar_ESO_ID
									) {
										setIsLoading(true);
										var redirectPayload = {
											Familiar_ESO_ID:
												row.Familiar_ESO_ID,
											User: payload.User,
											Department: payload.Department,
											Hospital: payload.Hospital,
											Email: payload.Email,
											Network_ID: row.Name,
											esoFiId: row.ESOFIID,
											Description: row.Description,
											Account_Type: row.AccountType,
											User_Type: row.UserType,
											User_Account_Type:
												payload.Account_Type,
											REQUEST_TYPE: sRequestType,
										};
										history.push({
											pathname: '/ModifyServiceAccount',
											state: redirectPayload,
										});
									}
								}

								return rowObject;
							}
						});

						setUsersServiceAccounts(accounts);
						// Allow search function for service accounts once exceeding a given number of accounts
						if (accounts.length > 10) {
							setShowFilter(true);
						}
					} else {
						setRedirect(true);
					}
				})
				.catch(error => {
					raiseErrorBanner(error, dispatch);
				});
			setIsLoading(false);
		}
	};

	/**
	 * Handler to determine the validity of the justification textbox.
	 */
	const onJustificationHandler = e => {
		if (e.target.value.match('^(?:[A-Za-z ]|)+$')) {
			setJustification(e.target.value);
		}
	};

	/**
	 * Terminate option for the selected service account.
	 */
	let terminateModal = sOpenTerminateModal && (
		<Dialog
			open={sOpenTerminateModal}
			onClose={() => {
				setOpenTerminateModal(false);
				setJustification([]);
			}}
		>
			<DialogTitle>Warning</DialogTitle>
			<DialogContent>
				All privileges associated with the UPMC Service Account will be
				terminated ASAP.
				<br></br>
				<br></br>
				Remarks:
				<input
					type='text'
					className={CSS_CLASSES.CONTROL}
					value={sJustification}
					onChange={onJustificationHandler}
					maxLength='200'
					height={20}
				/>
			</DialogContent>
			<DialogActions>
				<Button
					variant='text'
					onClick={() => setOpenTerminateModal(false)}
				>
					Cancel
				</Button>
				<Button
					variant='text'
					disabled={sJustification.length < 1}
					onClick={() => {
						setOpenTerminateModal(false);
						onTerminateRedirectHandler(sRowInfo);
					}}
				>
					Submit
				</Button>
			</DialogActions>
		</Dialog>
	);

	/**
	 * Reactivate option for the selected service account.
	 */
	let reactivateModal = sOpenReactivateModal && (
		<Dialog
			open={sOpenReactivateModal}
			onClose={() => {
				setOpenReactivateModal(false);
			}}
		>
			<DialogTitle>Service Account Reactivation</DialogTitle>
			<DialogContent>
				By reactivating this service account, you will also reactivate
				the following associated individual accounts:
				<br></br>
				<br></br>
				<div className={classes[CSS_CLASSES.DATATABLE]}>
					<DataTable
						actions={[]}
						headers={[
							...appRowHeaders.map(header => {
								return { Value: header };
							}),
						]}
						rows={sUsersServiceAccountApplications}
					/>
				</div>
			</DialogContent>
			<DialogActions>
				<Button
					variant='text'
					onClick={() => setOpenReactivateModal(false)}
				>
					Cancel
				</Button>
				<Button
					variant='text'
					onClick={() => {
						setOpenReactivateModal(false);
						onReactivateRedirectHandler(sRowInfo);
					}}
				>
					Proceed
				</Button>
			</DialogActions>
		</Dialog>
	);

	/**
	 * Event handler for Pop-up warning message appears when attempting to terminate.
	 * @param {Object} owner removed row in state object.
	 * @returns void
	 */
	let onRowTerminatePopupHandler = row => {
		setRowInfo(row);
		setOpenTerminateModal(true);
	};

	/**
	 * Event handler for Pop-up warning message appears when attempting to reactivate.
	 * @param {Object} owner removed row in state object.
	 * @returns void
	 */
	let onRowReactivatePopupHandler = row => {
		setRowInfo(row);

		let payload = {
			F_ESO_NTID: row.Cells.NetworkID.Value,
			SubUserType: row.AdditionalInfo.AccountType,
			F_ESO_ID: row.Cells.ESOFIID.Value,
		};

		axiosInstance
			.post('api/ModifyServiceAccount/Populate', WrapImsPacket(payload))
			.then(response => {
				const payload = response.data.Applications;
				const accounts = payload.map(row => {
					const validation = {
						Key: String(true),
						Value: null,
					};
					let rowObject = {
						ActionCells: [{}],
						Key: nanoid(),
						Cells: {
							Application_Name: {
								Value: row.Name,
								Validation: validation,
							},
							Key: {
								Value: row.Key,
							},
							Action: {
								Value: '',
								Validation: validation,
							},
						},
						Index: row.Index,
						State: row.State,
					};
					return rowObject;
				});
				setUsersServiceAccountApplications(
					accounts.filter(app => {
						return app.State === 'Reactivatable';
					}),
				);
				setOpenReactivateModal(true);
			})
			.catch(error => {
				raiseErrorBanner(error, dispatch);
			});
	};

	/**
	 * Event handler for data table row terminate.
	 * @param {Object} owner removed row in state object.
	 * @returns void
	 */
	let onTerminateRedirectHandler = row => {
		setIsLoading(true);

		axiosInstance
			.post(
				'/api/ServiceAccountTerminateTool/RevokeSubmission',
				WrapImsPacket({
					LoggedOnUser: user.ID,
					Justification: sJustification,
					RequestType: 'Single',
					Owners: [
						{
							Cells: {
								Service_Account_Familiar_ESO_ID: {
									Value: row.Cells.FamiliarESOID.Value,
								},
								Owner_Email: {
									Value: '',
								},
								Owner_Network_ID: {
									Value: '',
								},
								Service_Account_Network_ID: {
									Value: '',
								},
							},
						},
					],
				}),
			)
			.then(response => {
				setRequestTrackingNumber(
					Object.keys(response.data.payload.WIPHeaderDetails)[0],
				);
			})
			.catch(error => {
				raiseErrorBanner(error, dispatch);
			});
	};

	/**
	 * Event handler for data table row reactivate.
	 * @param {Object} owner removed row in state object.
	 * @returns void
	 */
	let onReactivateRedirectHandler = row => {
		setIsLoading(true);

		axiosInstance
			.post(
				'/api/ExistingServiceAccount/ReactivateSubmission',
				WrapImsPacket({
					LoggedOnUser: user.ID,
					RequestType: 'Single',
					Owners: [
						{
							Cells: {
								Service_Account_Familiar_ESO_ID: {
									Value: row.Cells.FamiliarESOID.Value,
								},
								Owner_Email: {
									Value: '',
								},
								Owner_Network_ID: {
									Value: '',
								},
								Service_Account_Network_ID: {
									Value: row.Cells.NetworkID.Value,
								},
							},
						},
					],
					Applications: sUsersServiceAccountApplications.map(app => {
						return {
							ID: app.Cells.Key.Value,
							Name: app.Cells.Application_Name.Value,
							Action: 'Reactivate',
						};
					}),
					AccountType: {
						ID: row.AdditionalInfo.AccountType,
						Name: row.Cells.AccountType.Value,
					},
					UserType: row.AdditionalInfo.UserType,
				}),
			)
			.then(response => {
				setReactivateRequestTrackingNumber(
					Object.keys(response.data.payload.WIPHeaderDetails)[0],
				);
			})
			.catch(error => {
				raiseErrorBanner(error, dispatch);
			});
	};

	/**
	 * Handler for modify action to redirect to the Modify Service Account page.
	 */
	let onRowModifyHandler = row => {
		setIsLoading(true);

		// Payload containing requestee details
		var payload = {
			Familiar_ESO_ID: row.Cells.FamiliarESOID.Value,
			User: sUserName,
			Department: sUserDepartment,
			Hospital: sUserHospital,
			Email: sUserEmail,
			Network_ID: row.Cells.NetworkID.Value,
			esoFiId: row.Cells.ESOFIID.Value,
			Description: row.Cells.AccountType.Value,
			Account_Type: row.AdditionalInfo.AccountType,
			User_Type: row.AdditionalInfo.UserType,
			User_Account_Type: sUserAccountType,
			REQUEST_TYPE: sRequestType,
		};
		history.push({
			pathname: '/ModifyServiceAccount',
			state: payload,
		});
	};

	/**
	 * Service accounts for the given owner/requestee along with the actions available for each.
	 */
	let serviceAccountDataTable = sUsersServiceAccounts &&
		sUsersServiceAccounts.length > 0 &&
		!sIsLoading && (
			<div className={classes[CSS_CLASSES.DATATABLE]}>
				<DataTable
					actions={[
						{
							onChangedHandler: (_, __, row) =>
								onRowTerminatePopupHandler(row),
							Type: ACTION_TYPES.REMOVE,
							disabled: sHideControl,
						},
						{
							onChangedHandler: (_, __, row) =>
								onRowModifyHandler(row),
							Type: ACTION_TYPES.EDIT,
						},
						{
							onChangedHandler: (_, __, row) =>
								onRowReactivatePopupHandler(row),
							Type: ACTION_TYPES.FLAG_PROBLEM,
							disabled: sHideControl,
						},
					]}
					headers={[
						...rowHeaders.map(header => {
							return { Value: header };
						}),
					]}
					iconLegend={iconLegend}
					label={'Accounts (' + sUsersServiceAccounts.length + ')'}
					rows={sUsersServiceAccounts.filter(a => {
						return !a.FilteredOut;
					})}
					indexed={sShowFilter}
					onTextFieldChangedHandler={e => setFilter(e.target.value)}
				/>
			</div>
		);

	/**
	 * Form body.
	 */
	let Form = (
		<Fragment>
			{terminateModal}
			{reactivateModal}
			<Card className={classes[CSS_CLASSES.PAGE_HEADER]}>
				<br></br>
				<Body1>
					Below are the existing service accounts for: <br></br>
					<b>
						{sUserName}
						<br></br>
						{sUserDepartment}
						<br></br>
						{sUserHospital}
						<br></br>
						{sUserEmail}
					</b>
				</Body1>
				<br></br>
			</Card>
			<WithLoadingHandler isLoading={sIsLoading}>
				{serviceAccountDataTable}
			</WithLoadingHandler>
			<Tooltip
				align={CONTENT_ALIGNMENT.RIGHT}
				content='Create New Service Account'
			>
				<FAB
					exited={sIsLoading}
					icon={<Icon icon='person_add_alt' />}
					onClick={() => {
						setIsLoading(true);
						var payload = {
							REQUEST_TYPE: sRequestType,
							requestorESOFIID: user.ID,
							Familiar_ESO_ID: requesteeFamiliarESOID,
							User: sUserName,
						};
						history.push({
							pathname: '/AddServiceAccount',
							state: payload,
						});
					}}
					text='Create'
				/>
			</Tooltip>
		</Fragment>
	);

	// Redirects requestor to add service account page if they requestee has no existing service accounts
	return sRedirect ? (
		<div>
			<Redirect
				to={{
					pathname: '/AddServiceAccount',
					state: {
						REQUEST_TYPE: sRequestType,
						requestorESOFIID: user.ID,
						Familiar_ESO_ID: requesteeFamiliarESOID,
						User: sUserName,
					},
				}}
			/>
		</div>
	) : (
		// Redirects to confirmation page when terminate is successful
		<div>
			{sRequestTrackingNumber ? (
				<div>
					<Redirect to={'/Confirm'} />
				</div>
			) : (
				<div>
					{sReactivateRequestTrackingNumber ? (
						<div>
							<Redirect
								to={
									'/RRS/Request_ModifyDetail_View.aspx?tn=' +
									sReactivateRequestTrackingNumber +
									'&REQUEST_TYPE=' +
									(sRequestType === 'For_Myself'
										? RequestTypes.SELF_SERVICE_ACCOUNT
										: RequestTypes.FOR_OTHERS_SERVICE_ACCOUNT)
								}
							/>
						</div>
					) : (
						<Base
							helpLink={
								'https://infonet.upmc.com/infonet/ls/content/3298180640090487/service-account-creation-and-management-pdf-1764800010'
							}
							loading={sIsLoading}
							title='Service Accounts'
						>
							{Form}
						</Base>
					)}
				</div>
			)}
		</div>
	);
};

export default ExistingServiceAccount;
